import { useState } from 'react';

import { Box, Button, ButtonGroup, Heading, Modal, TextButton } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertySummaryWebApiHandler_nextSteps,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useApplicationsFeeField } from '../../../hooks/useApplicationsFeeField';
import propertyUtils from '../../../utils/propertyUtils';

import {
  emitApplicationsDisableFromEnabledEvent,
  emitApplicationsEnableFromDisabledEvent,
} from '../../../analytics/events/modularListing';
import { useAuth } from '../../AuthProvider';
import ApplicationsFormField from '../../PostingPath/ApplicationsFormField';
import ApplicationFeeFormField from '../../PostingPath/ApplicationsFormField/ApplicationFeeFormField';

import type { ChangeEvent } from 'react';

type Props = {
  listingAlias: string;
  isFeed?: boolean;
  isIntroModal?: boolean;
  onClose: () => void;
};

const ApplicationsModal = ({ listingAlias, isFeed = false, isIntroModal = false, onClose }: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview({ listingAlias });

  const isMultifamily = overviewData?.isMultifamily ?? false;
  const initialIsApplicationsAccepted = overviewData?.isApplicationsAccepted;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: overviewData });

  const [radioValue, setRadioValue] = useState<string>(
    initialIsApplicationsAccepted === null || initialIsApplicationsAccepted === undefined
      ? ''
      : String(initialIsApplicationsAccepted),
  );
  const [serverError, setServerError] = useState<string>('');

  const { canAccessSFCostsAndFees, feeAmount, feeError, setFeeError, validateFeeAmount, handleFeeChange } =
    useApplicationsFeeField(listingAlias, { isMultifamily, isFeed });

  const { mutate: editIsApplicationsAccepted, isPending: isSaving } =
    useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted',
        },
        invalidates: ['users-properties-webAnalytics-get', 'sf-applicationFee'],
      },
    });

  const save = (newRadioValue: string, newFeeAmount: string) => {
    if (canAccessSFCostsAndFees && newRadioValue === 'false') {
      const err = validateFeeAmount(newFeeAmount);
      if (err) {
        setFeeError(err);
        return;
      }
    }

    setServerError('');

    const prevIsApplicationsAccepted = initialIsApplicationsAccepted;
    const nextValue = newRadioValue === 'true';

    const params: {
      propertyId: string;
      isApplicationsAccepted: boolean;
      inviteOnly: boolean;
      applicationFeeAmount?: number;
    } = {
      propertyId: listingAlias,
      isApplicationsAccepted: nextValue,
      inviteOnly: false,
    };

    if (canAccessSFCostsAndFees && newRadioValue === 'false' && newFeeAmount !== '') {
      params.applicationFeeAmount = Number(newFeeAmount);
    }

    editIsApplicationsAccepted(
      { getParams: params },
      {
        onSuccess: () => {
          const csParams = { currentProperty: { listingAlias }, user: user ?? {} };

          if (nextValue && prevIsApplicationsAccepted === false) {
            emitApplicationsEnableFromDisabledEvent({ ...csParams, triggerLocation: 'zrm_property_overview' });
          } else if (!nextValue && prevIsApplicationsAccepted === true) {
            emitApplicationsDisableFromEnabledEvent({ ...csParams, triggerLocation: 'zrm_property_overview' });
          }

          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
          });
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_nextSteps.getKey({ listingAlias }),
          });
          onClose();
        },
        onError: (error) => {
          const err = error as { message?: string };
          setServerError(err?.message ?? 'Unable to update your applications setting. Try again.');
        },
      },
    );
  };

  const handleRadioChange = (event: ChangeEvent<Element>) => {
    const newValue = (event.target as HTMLInputElement).value;
    setRadioValue(newValue);
    setServerError('');
    setFeeError('');
  };

  return (
    <>
      <Modal
        open={true}
        onOpenChange={(open) => !open && onClose()}
        size={{ base: 'fullScreen', md: 'md' }}
        header={<Heading level={2}>Zillow applications</Heading>}
        body={
          <Box css={{ padding: '600' }} data-testid="EnableApplicationsModal">
            <ApplicationsFormField
              isIntroModal={isIntroModal}
              isRoomForRent={isRoomForRent}
              radioValue={radioValue}
              onRadioChange={handleRadioChange}
              serverError={serverError}
              disabled={isSaving}
            />

            {canAccessSFCostsAndFees && radioValue === 'false' && (
              <Box css={{ paddingLeft: '800', marginTop: '400' }}>
                <ApplicationFeeFormField
                  value={feeAmount}
                  onChange={handleFeeChange}
                  error={feeError}
                  disabled={isSaving}
                />
              </Box>
            )}
          </Box>
        }
        footer={
          <ButtonGroup aria-label="Actions">
            <TextButton onClick={onClose}>Cancel</TextButton>
            <Button tone="brand" emphasis="filled" onClick={() => save(radioValue, feeAmount)} loading={isSaving}>
              Save
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};

export default ApplicationsModal;
